/**
 * Shared helper for calling the `manageFamily` admin Lambda (#127 slice 4).
 *
 * API contract (`.agents/tasks/127-manage-family-contract.md`, binding):
 *   manageFamily(action: String!, payload: AWSJSON!): AWSJSON
 * The payload is sent as a JSON string (AWSJSON accepts a JSON-encoded
 * string), and the response is parsed with `parseLambdaResponse`, which
 * already handles the double JSON encoding AppSync Lambda mutations use.
 *
 * The generated client type may not know about `manageFamily` yet (the
 * backend is being built in parallel against the same contract), so this
 * casts through `any` the same way other admin mutations in this codebase
 * do for not-yet-generated Lambda mutations.
 */
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

export type ManageFamilyAction = 'ADD_PERSON' | 'GROUP' | 'SET_PRIMARY' | 'REMOVE';

export interface ManageFamilySuccess {
  success: true;
  action: ManageFamilyAction;
  familyGroupId?: string;
  changedMemberIds?: string[];
  members?: Array<{ id: string; memberNumber?: string }>;
  amountOwedCents?: number;
  message?: string;
}

export interface ManageFamilyFailure {
  success: false;
  error: string;
  message: string;
}

export type ManageFamilyResult = ManageFamilySuccess | ManageFamilyFailure;

/**
 * Calls `manageFamily` with the given action and payload, returning the
 * parsed response. Never throws for a server-reported failure (callers
 * check `result.success`); throws only for a transport failure, where no
 * usable response could be parsed at all.
 */
export async function callManageFamily(
  action: ManageFamilyAction,
  payload: Record<string, unknown>
): Promise<ManageFamilyResult> {
  const raw = await (client.mutations as any).manageFamily({
    action,
    payload: JSON.stringify(payload),
  });
  const parsed = parseLambdaResponse<ManageFamilyResult>(raw?.data);
  if (!parsed) {
    throw new Error('No response received from the server. Please try again.');
  }
  return parsed;
}
